import React, { useMemo, useState, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { estimasiPendapatanDariM, hitungKomponenDariPendapatan, hitungSetoran } from '../utils/iuranCalculator';

const SetoranForm = () => {
  const { anggota, setoran, tambahSetoran, updateSetoran, hapusSetoran } = useContext(AppContext);
  
  const monthOptions = [
    { value: '01', label: 'Januari' },
    { value: '02', label: 'Februari' },
    { value: '03', label: 'Maret' },
    { value: '04', label: 'April' },
    { value: '05', label: 'Mei' },
    { value: '06', label: 'Juni' },
    { value: '07', label: 'Juli' },
    { value: '08', label: 'Agustus' },
    { value: '09', label: 'September' },
    { value: '10', label: 'Oktober' },
    { value: '11', label: 'November' },
    { value: '12', label: 'Desember' },
  ];
  
  const formatMonthLabel = (monthValue) => {
    const [year, month] = String(monthValue).split('-');
    const monthName = monthOptions.find((m) => m.value === month)?.label || month;
    return `${monthName} ${year}`;
  };
  
  const getMonthParts = (monthValue) => {
    const [year, month] = String(monthValue).split('-');
    return { year, month };
  };
  
  const buildMonthValue = (year, month) => `${year}-${month}`;
  const [form, setForm] = useState({
    anggota_id: '',
    bulan: new Date().toISOString().slice(0, 7),
    iuran_bulanan: 0,
    iuran_idi: 0,
    iuran_gasibu: 0,
  });
  const [hasilEstimasi, setHasilEstimasi] = useState(null);
  const [koreksiBulan, setKoreksiBulan] = useState(new Date().toISOString().slice(0, 7));
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ iuran_bulanan: 0, iuran_idi: 0, iuran_gasibu: 0 });
  const [savingId, setSavingId] = useState(null);

  const transaksiKoreksi = useMemo(() => {
    const grouped = setoran.reduce((acc, item) => {
      const key = `${item.anggota_id}-${item.bulan}`;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    return setoran
      .filter((item) => item.bulan === koreksiBulan)
      .map((item) => {
        const anggotaData = anggota.find((a) => a.id === item.anggota_id);
        const key = `${item.anggota_id}-${item.bulan}`;
        return {
          ...item,
          namaAnggota: anggotaData?.nama || `ID ${item.anggota_id}`,
          jumlahAnak: anggotaData?.jumlah_anak ?? '-',
          isDuplicate: grouped[key] > 1,
        };
      })
      .sort((a, b) => b.id - a.id);
  }, [setoran, anggota, koreksiBulan]);

  const handleEstimasi = () => {
    const { iuran_bulanan, iuran_idi, iuran_gasibu } = form;
    if (!iuran_bulanan || iuran_bulanan <= 0) {
      alert('Masukkan Iuran Bulanan terlebih dahulu');
      return;
    }
    const pendapatan = estimasiPendapatanDariM(iuran_bulanan);
    const komponen = hitungKomponenDariPendapatan(pendapatan);
    const setoranDetail = hitungSetoran(pendapatan, iuran_idi, iuran_bulanan, iuran_gasibu);
    setHasilEstimasi({ pendapatan, komponen, setoranDetail });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.anggota_id) return alert('Pilih anggota');
    if (!form.iuran_bulanan || form.iuran_bulanan <= 0) return alert('Masukkan Iuran Bulanan');

    // Kirim data ke backend (backend akan hitung ulang, tapi kita kirim apa adanya)
    const setoranBaru = {
      anggota_id: parseInt(form.anggota_id),
      bulan: form.bulan,
      iuran_bulanan: form.iuran_bulanan,
      iuran_idi: form.iuran_idi,
      iuran_gasibu: form.iuran_gasibu,
    };
    try {
      await tambahSetoran(setoranBaru);
      setForm({ ...form, iuran_bulanan: 0, iuran_idi: 0, iuran_gasibu: 0 });
      setHasilEstimasi(null);
      alert('Setoran berhasil disimpan');
    } catch (err) {
      alert('Gagal menyimpan setoran');
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditForm({
      iuran_bulanan: Number(item.iuran_bulanan) || 0,
      iuran_idi: Number(item.iuran_idi) || 0,
      iuran_gasibu: Number(item?.komponen?.iuranGasibu) || 0,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ iuran_bulanan: 0, iuran_idi: 0, iuran_gasibu: 0 });
  };

  const handleSaveEdit = async (item) => {
    if (!editForm.iuran_bulanan || editForm.iuran_bulanan <= 0) {
      alert('Iuran bulanan harus lebih dari 0');
      return;
    }

    try {
      setSavingId(item.id);
      await updateSetoran(item.id, {
        iuran_bulanan: Number(editForm.iuran_bulanan),
        iuran_idi: Number(editForm.iuran_idi) || 0,
        iuran_gasibu: Number(editForm.iuran_gasibu) || 0,
      });
      alert('Setoran berhasil diupdate');
      cancelEdit();
    } catch (err) {
      alert(err?.response?.data?.error || 'Gagal update setoran');
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (item) => {
    const confirmed = window.confirm(
      `Hapus setoran ${item.namaAnggota} bulan ${item.bulan}? Tindakan ini tidak bisa dibatalkan.`
    );

    if (!confirmed) return;

    try {
      setSavingId(item.id);
      await hapusSetoran(item.id);
      alert('Setoran berhasil dihapus');
    } catch (err) {
      alert(err?.response?.data?.error || 'Gagal menghapus setoran');
    } finally {
      setSavingId(null);
      if (editingId === item.id) {
        cancelEdit();
      }
    }
  };

  const formatRp = (val) => `Rp ${(Number(val) || 0).toLocaleString()}`;

  return (
    <div className="p-4">
      <div className="border rounded-lg p-4 bg-blue-50 mb-8">
        <h3 className="text-xl font-bold text-blue-700 mb-3">Catat Setoran Iuran</h3>
        
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block mb-1 font-semibold text-sm">Pilih Anggota</label>
            <select
              value={form.anggota_id}
              onChange={e => setForm({ ...form, anggota_id: e.target.value, iuran_bulanan: 0, iuran_idi: 0, iuran_gasibu: 0, hasilEstimasi: null })}
              className="border p-2 rounded w-full"
              required
            >
              <option value="">-- Pilih Anggota --</option>
              {anggota.map(a => (
                <option key={a.id} value={a.id}>{a.nama} (Anak: {a.jumlah_anak})</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block mb-1 font-semibold text-sm">Periode</label>
            <div className="flex gap-2">
              <select
                value={getMonthParts(form.bulan).month}
                onChange={(e) => setForm({ ...form, bulan: buildMonthValue(getMonthParts(form.bulan).year, e.target.value) })}
                className="border p-2 rounded flex-1"
                required
              >
                {monthOptions.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <select
                value={getMonthParts(form.bulan).year}
                onChange={(e) => setForm({ ...form, bulan: buildMonthValue(e.target.value, getMonthParts(form.bulan).month) })}
                className="border p-2 rounded w-24"
                required
              >
                {[new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1].map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block mb-1 font-semibold text-sm">Iuran Bulanan</label>
            <input
              type="number"
              value={form.iuran_bulanan}
              onChange={e => setForm({ ...form, iuran_bulanan: Number(e.target.value), hasilEstimasi: null })}
              className="border p-2 rounded w-full"
              placeholder="Contoh: 300000"
              required
            />
          </div>

          <div>
            <label className="block mb-1 font-semibold text-sm">IDI</label>
            <input
              type="number"
              value={form.iuran_idi}
              onChange={e => setForm({ ...form, iuran_idi: Number(e.target.value), hasilEstimasi: null })}
              className="border p-2 rounded w-full"
              placeholder="Iuran IDI (terpisah)"
            />
          </div>

          <div>
            <label className="block mb-1 font-semibold text-sm">Gasibu/Sumatera/Dll</label>
            <input
              type="number"
              value={form.iuran_gasibu}
              onChange={e => setForm({ ...form, iuran_gasibu: Number(e.target.value), hasilEstimasi: null })}
              className="border p-2 rounded w-full"
              placeholder="Contoh: 10000"
            />
          </div>

          <button type="button" onClick={handleEstimasi} className="bg-purple-600 hover:bg-purple-700 text-white p-2 rounded w-full font-semibold">
            Estimasi Pendapatan & Komponen
          </button>

          {hasilEstimasi && (
            <div className="mt-3 p-3 bg-white text-sm border rounded">
              <p><strong>Estimasi pendapatan:</strong> Rp {hasilEstimasi.pendapatan.toLocaleString()}</p>
              <p><strong>Status Muzaki:</strong> {hasilEstimasi.pendapatan >= 7640144 ? 'MUZAKI (kena IIP 2.5%)' : 'BELUM MUZAKI'}</p>
              <hr className="my-2" />
              <p>IBK (1.5%): Rp {hasilEstimasi.komponen.ibk.toLocaleString()}</p>
              <p>IBS (0.5%): Rp {hasilEstimasi.komponen.ibs.toLocaleString()}</p>
              <p>IBP (2%): Rp {hasilEstimasi.komponen.ibp.toLocaleString()}</p>
              <p>IBKs (0.5%): Rp {hasilEstimasi.komponen.ibks.toLocaleString()}</p>
              <p>IIP (2.5%): Rp {hasilEstimasi.komponen.iip.toLocaleString()}</p>
              <p>Lainnya (pembulatan): Rp {hasilEstimasi.komponen.lainnya.toLocaleString()}</p>
              <p><strong>Total Iuran Bulanan (M):</strong> Rp {hasilEstimasi.komponen.iuranBulanan.toLocaleString()}</p>
              <hr className="my-2" />
              <p className="font-bold">Rincian Setoran ke Yayasan:</p>
              <p>✓ Setoran ke Pusat: <strong>Rp {hasilEstimasi.setoranDetail.setoranPusat.toLocaleString()}</strong></p>
              <p>✓ Gasibu/Sumatera/Dll: Rp {hasilEstimasi.setoranDetail.gasibu.toLocaleString()}</p>
              <p>✓ Kas Kelas: Rp {hasilEstimasi.setoranDetail.kasKelas.toLocaleString()}</p>
            </div>
          )}

          <button type="submit" className="bg-green-600 hover:bg-green-700 text-white p-2 rounded w-full font-semibold">Simpan Setoran</button>
        </form>
      </div>

      <div className="mt-8 border rounded-lg p-4 bg-red-50">
        <h3 className="text-xl font-bold text-red-700 mb-2">Koreksi Setoran Iuran</h3>

        <div className="mb-3 flex items-center gap-2 flex-wrap">
          <label className="font-semibold">Periode:</label>
          <select
            value={getMonthParts(koreksiBulan).month}
            onChange={(e) => setKoreksiBulan(buildMonthValue(getMonthParts(koreksiBulan).year, e.target.value))}
            className="border p-2 rounded"
          >
            {monthOptions.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <select
            value={getMonthParts(koreksiBulan).year}
            onChange={(e) => setKoreksiBulan(buildMonthValue(e.target.value, getMonthParts(koreksiBulan).month))}
            className="border p-2 rounded w-24"
          >
            {[new Date().getFullYear() - 1, new Date().getFullYear(), new Date().getFullYear() + 1].map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        {transaksiKoreksi.length === 0 ? (
          <p className="text-sm text-gray-600">Tidak ada transaksi setoran pada periode ini.</p>
        ) : (
          <div className="overflow-x-auto border rounded bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-red-100">
                <tr>
                  <th className="border p-2 text-left">ID</th>
                  <th className="border p-2 text-left">Nama</th>
                  <th className="border p-2 text-center">Jml Anak</th>
                  <th className="border p-2 text-center">Bulan</th>
                  <th className="border p-2 text-right">Iuran Bulanan</th>
                  <th className="border p-2 text-right">IDI</th>
                  <th className="border p-2 text-right">Gasibu/Sumatera/Dll</th>
                  <th className="border p-2 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {transaksiKoreksi.map((item) => {
                  const isEditing = editingId === item.id;
                  const isBusy = savingId === item.id;

                  return (
                    <tr key={item.id} className={item.isDuplicate ? 'bg-red-50' : 'hover:bg-gray-50'}>
                      <td className="border p-2">{item.id}</td>
                      <td className="border p-2">{item.namaAnggota}</td>
                      <td className="border p-2 text-center">{item.jumlahAnak}</td>
                      <td className="border p-2 text-center">{formatMonthLabel(item.bulan)}</td>
                      <td className="border p-2 text-right">
                        {isEditing ? (
                          <input
                            type="number"
                            className="border p-1 rounded w-32 text-right"
                            value={editForm.iuran_bulanan}
                            onChange={(e) => setEditForm((prev) => ({ ...prev, iuran_bulanan: Number(e.target.value) }))}
                          />
                        ) : (
                          formatRp(item.iuran_bulanan)
                        )}
                      </td>
                      <td className="border p-2 text-right">
                        {isEditing ? (
                          <input
                            type="number"
                            className="border p-1 rounded w-32 text-right"
                            value={editForm.iuran_idi}
                            onChange={(e) => setEditForm((prev) => ({ ...prev, iuran_idi: Number(e.target.value) }))}
                          />
                        ) : (
                          formatRp(item.iuran_idi)
                        )}
                      </td>
                      <td className="border p-2 text-right">
                        {isEditing ? (
                          <input
                            type="number"
                            className="border p-1 rounded w-32 text-right"
                            value={editForm.iuran_gasibu}
                            onChange={(e) => setEditForm((prev) => ({ ...prev, iuran_gasibu: Number(e.target.value) }))}
                          />
                        ) : (
                          formatRp(item?.komponen?.iuranGasibu)
                        )}
                      </td>
                      <td className="border p-2 text-center">
                        <div className="flex justify-center gap-2">
                          {isEditing ? (
                            <>
                              <button
                                onClick={() => handleSaveEdit(item)}
                                disabled={isBusy}
                                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-3 py-1 rounded"
                              >
                                {isBusy ? 'Menyimpan...' : 'Simpan'}
                              </button>
                              <button
                                onClick={cancelEdit}
                                disabled={isBusy}
                                className="bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 text-white px-3 py-1 rounded"
                              >
                                Batal
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => startEdit(item)}
                                disabled={isBusy}
                                className="bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-400 text-white px-3 py-1 rounded"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(item)}
                                disabled={isBusy}
                                className="bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white px-3 py-1 rounded"
                              >
                                Hapus
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SetoranForm;